import { useState } from 'react';

import Button from 'components/Button';
import Popover from 'components/Popover';
import type { FieldKey } from 'hooks';
import type { Review as ReviewModel } from 'models/api';
import type { TemplateString } from 'types';

import { useReviewContext } from '../ProductDetailsContext';

import Review from './Review';
import ReviewImagesDialog from './ReviewImagesDialog';
import ReviewImageThumbnails from './ReviewImageThumbnails';

interface Props {
	className?: string;
	dialogLabel: string;
	feedbackDoneText: string;
	flagAltText: FieldKey | TemplateString;
	readReviewButtonText: string;
	reportButtonText: string;
	reviewImageButtonLabel: string;
	reviewProductNotRecommendedText: FieldKey | TemplateString;
	reviewProductRecommendedText: FieldKey | TemplateString;
	reviewShowOriginalTextButtonText: string;
	reviewShowTranslationToLanguageButtonText: FieldKey | TemplateString;
	reviewTranslatedFromLanguageText: FieldKey | TemplateString;
	reviewVerifiedBuyerLabel: string;
	singleReviewPopoverTitle: string;
	upvoteButtonText: string;
	viewReviewImagesButtonText: string;
}

/** Review images opener button and dialog. */
export default function ReviewImages({
	className,
	dialogLabel,
	feedbackDoneText,
	flagAltText,
	readReviewButtonText,
	reportButtonText,
	reviewImageButtonLabel,
	reviewProductNotRecommendedText,
	reviewProductRecommendedText,
	reviewShowOriginalTextButtonText,
	reviewShowTranslationToLanguageButtonText,
	reviewTranslatedFromLanguageText,
	reviewVerifiedBuyerLabel,
	singleReviewPopoverTitle,
	upvoteButtonText,
	viewReviewImagesButtonText,
}: Props) {
	const { reviewImages } = useReviewContext();
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [selectedImageIndex, setSelectedImageIndex] = useState(0);
	const [selectedReview, setSelectedReview] = useState<
		ReviewModel | undefined
	>();
	const imageCount = reviewImages.length;

	if (imageCount === 0) {
		return null;
	}

	return (
		<div className={className}>
			<ReviewImageThumbnails
				buttonLabel={reviewImageButtonLabel}
				images={reviewImages.slice(0, 3)}
				onImageClick={({ index }) => {
					setSelectedImageIndex(index);
					setIsDialogOpen(true);
				}}
			/>

			<Button
				className="mt-4"
				onClick={() => setIsDialogOpen(true)}
				leadingIcon="gallery"
			>
				{viewReviewImagesButtonText} ({imageCount})
			</Button>

			<ReviewImagesDialog
				id="review-images-dialog"
				label={dialogLabel}
				isOpen={isDialogOpen}
				onClose={() => setIsDialogOpen(false)}
				onReviewButtonClick={setSelectedReview}
				images={reviewImages}
				readReviewButtonText={readReviewButtonText}
				selectedImageIndex={selectedImageIndex}
				setSelectedImageIndex={setSelectedImageIndex}
			/>

			<Popover
				isOpen={Boolean(selectedReview)}
				onClose={() => setSelectedReview(undefined)}
				title={singleReviewPopoverTitle}
			>
				{selectedReview && (
					<Review
						review={selectedReview}
						showImages={false}
						feedbackDoneText={feedbackDoneText}
						flagAltText={flagAltText}
						reportButtonText={reportButtonText}
						reviewImageButtonLabel={reviewImageButtonLabel}
						upvoteButtonText={upvoteButtonText}
						verifiedBuyerLabel={reviewVerifiedBuyerLabel}
						showOriginalTextButtonText={reviewShowOriginalTextButtonText}
						showTranslationToLanguageButtonText={
							reviewShowTranslationToLanguageButtonText
						}
						translatedFromLanguageText={reviewTranslatedFromLanguageText}
						productRecommendedText={reviewProductRecommendedText}
						productNotRecommendedText={reviewProductNotRecommendedText}
					/>
				)}
			</Popover>
		</div>
	);
}
ReviewImages.displayName = 'ReviewImages';
